import type { KeyboardEvent, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Platform, View } from 'react-native';

import { graphql as tadaGraphql } from '@savvy/deprecated-shared/gql-compat';
import { useMutationWithErrorLogging } from '@savvy/deprecated-shared/hooks/useMutationWithErrorLogging';
import { XLink } from '@savvy/deprecated-shared/x-components/x-link';
import { XStack } from '@savvy/deprecated-shared/x-components/x-stack';
import { XTextField } from '@savvy/deprecated-shared/x-components/x-text-field';
import { toast } from '@savvy/deprecated-shared/x-components/x-toast';
import { XTypography } from '@savvy/deprecated-shared/x-components/x-typography';

const otpFormMutation = tadaGraphql(/* GraphQL */ `
  mutation OtpForm($input: ResendOtpInput!) {
    resendOtp(input: $input) {
      error
    }
  }
`);

type Props = {
  readonly userEmail?: string;
  readonly userInputPassword: string;
  readonly onSendEmail?: () => Promise<string | null | undefined>;
  readonly onSubmit: (otp: string) => Promise<boolean>;
};

export function XOtpForm({ userEmail, userInputPassword, onSendEmail, onSubmit }: Props) {
  const [otpArray, setOtpArray] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const focusTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [resendOtp] = useMutationWithErrorLogging(otpFormMutation, {
    variables: {
      input: {
        channel: 'sms',
        userEmail: userEmail ?? '',
        currentPassword: userInputPassword,
      },
    },
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      inputRefs.current[0]?.focus();
    }, 300);
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  function onPaste(e: React.ClipboardEvent, index: number) {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').split('');
    if (paste.every((item) => !isNaN(Number(item)))) {
      const newInputValue = [...otpArray];
      for (let i = 0; i < paste.length; i++) {
        if (index + i < otpArray.length) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: This rule was recently enabled for the repo
          newInputValue[index + i] = paste[i]!;
        }
      }
      setOtpArray(newInputValue);
    }
  }

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>, index: number) => {
    const keyCode = parseInt(e.key);
    if ((e.metaKey && e.key === 'v') || (keyCode >= 0 && keyCode <= 9)) {
      return;
    } else if (e.key === 'ArrowRight') {
      inputRefs.current[index + 1]?.focus();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    }
  };

  const onChangeText = (input: string, index: number) => {
    if (input.length === 6 && !isNaN(Number.parseInt(input))) {
      setOtpArray(input.split(''));
      return;
    } else if (input.length > 1) {
      return;
    }
    if (input === '' || !isNaN(Number.parseInt(input))) {
      setOtpArray((preValue) => {
        const newArray = [...preValue];
        newArray[index] = input;
        return newArray;
      });

      if (input !== '' && index < otpArray.length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const onKeyUp = (e: KeyboardEvent<HTMLDivElement>, index: number) => {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      setOtpArray((prevValue) => {
        const newArray = [...prevValue];
        return newArray;
      });

      if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }
  };
  const clearInputs = () => {
    setOtpArray(['', '', '', '', '', '']);
    inputRefs.current[0]?.focus();
  };
  const handleResendOtp = async () => {
    setIsSubmitting(true);
    const { data } = await resendOtp();
    if (data?.resendOtp?.error) {
      setErrMsg(data.resendOtp.error);
    } else {
      setErrMsg(null);
      toast.success('New code sent!');
    }
    setIsSubmitting(false);
    clearInputs();
    setTimeout(() => {
      inputRefs.current[0]?.focus();
    }, 1);
  };

  const onSubmitRef = useRef(onSubmit);
  useEffect(() => {
    onSubmitRef.current = onSubmit;
  }, [onSubmit]);
  const handleSubmit = (otp: string) => onSubmitRef.current(otp);

  useEffect(() => {
    return () => {
      if (focusTimeoutIdRef.current !== null) {
        clearTimeout(focusTimeoutIdRef.current);
        focusTimeoutIdRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (otpArray.every((value) => value !== '')) {
      setIsSubmitting(true);
      void handleSubmit(otpArray.join('')).then((success) => {
        if (cancelled) return;
        if (!success) {
          setErrMsg('Invalid or expired code');
          clearInputs();
          if (focusTimeoutIdRef.current !== null) {
            clearTimeout(focusTimeoutIdRef.current);
          }
          const timeoutId = setTimeout(() => {
            inputRefs.current[0]?.focus();
            if (focusTimeoutIdRef.current === timeoutId) {
              focusTimeoutIdRef.current = null;
            }
          }, 1);
          setIsSubmitting(false);
          focusTimeoutIdRef.current = timeoutId;
        } else {
          setErrMsg(null);
        }
      });
    }
    return () => {
      cancelled = true;
    };
  }, [otpArray]);

  const handleSendEmail = async () => {
    setIsSubmitting(true);
    const emailErr = await onSendEmail?.();
    if (emailErr) {
      setErrMsg(emailErr);
    } else {
      setErrMsg(null);
    }
    setIsSubmitting(false);
  };

  return (
    <>
      <XStack direction="column" maxWidth={300} marginLeft="auto" marginRight="auto" spacing={8}>
        <XStack direction="row" spacing={8}>
          {[0, 1, 2, 3, 4, 5].map((i) => {
            const value = otpArray[i] ?? '';
            return (
              <XTextField
                key={i}
                disabled={isSubmitting}
                // eslint-disable-next-line typescript/consistent-type-assertions, typescript/no-explicit-any -- FIXME: This rule was recently enabled for the repo
                inputRef={((el?: HTMLInputElement) => el && (inputRefs.current[i] = el)) as unknown as RefObject<any>}
                inputMode="numeric"
                maxLength={6}
                inputStyle={{
                  paddingStart: 4,
                  paddingBottom: 16,
                }}
                style={{
                  display: 'flex',
                  flexShrink: 1,
                  height: Platform.OS === 'web' ? 60 : 48,
                  maxWidth: 50,
                }}
                type="text"
                value={String(value)}
                onChangeText={(text) => {
                  onChangeText(text, i);
                }}
                onKeyUp={(e) => {
                  onKeyUp(e, i);
                }}
                onKeyDown={(e) => {
                  onKeyDown(e, i);
                }}
                onPaste={(e) => {
                  onPaste(e, i);
                }}
                autoComplete="one-time-code"
                accessKey={String(i)}
              />
            );
          })}
        </XStack>
        {errMsg && !isSubmitting && (
          <XTypography color="red" variant={Platform.OS === 'web' ? 'body2' : 'caption'}>
            {errMsg}
          </XTypography>
        )}
      </XStack>
      <View>
        <XTypography variant={Platform.OS === 'web' ? 'body1' : 'body2'} style={{ cursor: 'pointer', marginTop: 32 }}>
          Didn&apos;t receive your code?{' '}
          <XLink
            style={{ fontWeight: '600' }}
            onClick={() => {
              void handleResendOtp();
            }}
            variant={Platform.OS === 'web' ? 'body1' : 'body2'}
          >
            Get a new one
          </XLink>{' '}
          {onSendEmail ? (
            <>
              <XTypography
                variant="body1"
                style={{
                  cursor: 'pointer',
                }}
              >
                or request an{' '}
              </XTypography>
              <XLink
                style={{ fontWeight: '600' }}
                onClick={() => {
                  void handleSendEmail();
                }}
              >
                email
              </XLink>
            </>
          ) : null}
        </XTypography>
      </View>
    </>
  );
}
